import { Table } from "@/v2/components/table/Table";
import {
  ExploreAggColumnType,
  ExploreColumnType,
  ExploreGroupByColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import { useExploreColumns } from "@/v2/entities/explore/columns/useExploreColumns";
import {
  useExploreAggInteractions,
  useExploreInteractions,
  useExploreApi,
} from "@/v2/entities/explore/Explore.api";
import {
  AllowedAggOrderBy,
  AllowedInteractionOrderBy,
} from "@/v2/features/explore/explore-display-options/ExploreDisplayOptions.model";
import ExploreNewViewForm from "@/v2/features/explore/ExploreNewViewForm";
import { useExploreCustomFields } from "@/v2/features/explore/useExploreCustomFields";
import { useExploreConversationCustomFieldsForView } from "@/v2/features/explore/useExploreConversationCustomFieldsForView";
import { useExploreViewForm } from "@/v2/features/explore/useExploreViewForm";
import { Box, Flex, Text } from "@radix-ui/themes";
import { SortingState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export interface PreviewState {
  source: "tickets" | "conversations";
  filterString?: string;
  orderBy?: AllowedInteractionOrderBy | AllowedAggOrderBy;
  orderDir?: "asc" | "desc";
  groupBy?: ExploreGroupByColumnType;
  columns?: ExploreColumnType[];
  aggColumns?: ExploreAggColumnType[];
  aggOrderBy?: AllowedAggOrderBy | null;
  aggOrderDir?: "asc" | "desc" | null;
}

export function ExploreViewForm() {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const viewSlug = params.slug as string | undefined;
  const mode = viewSlug ? "update" : "create";

  const { getViewBySlug, updateView, createView } = useExploreApi();
  const customFields = useExploreCustomFields();
  const conversationCustomFieldsForView =
    useExploreConversationCustomFieldsForView();
  // Fetch the view to edit if in update mode
  const { data: view, isLoading: viewLoading } = useQuery({
    queryKey: ["explore-view-by-slug", viewSlug],
    queryFn: () => getViewBySlug(viewSlug!),
    enabled: mode === "update",
  });

  const {
    formData,
    displayOptions,
    filters,
    filter,
    errors,
    isLoading,
    handleSubmit,
    handleCancel,
    handleFieldChange,
    handleDisplayOptionsChange,
    handleFiltersChange,
    handleSortingChange,
  } = useExploreViewForm({
    mode,
    initialData: view,
    onSubmit: async (data) => {
      if (mode === "update" && view) {
        const updatedView = await updateView(view.id, data);
        toast.success("View updated successfully");
        queryClient.removeQueries(["explore-view-by-slug", view.slug]);
        queryClient.setQueryData(
          ["explore-view-by-slug", updatedView.slug],
          updatedView
        );
        queryClient.invalidateQueries(["explore-views"]);
        navigate(
          { pathname: `/explore/views/${updatedView.slug}`, search: "" },
          { replace: true }
        );
      } else {
        const newView = await createView(data);
        toast.success("View created successfully");
        navigate(`/explore/views/${newView.slug}`);
      }
    },
    onCancel: () => {
      if (viewSlug) {
        navigate(`/explore/views/${viewSlug}`);
      } else {
        navigate("/explore/views");
      }
    },
  });

  const activeSorting = useMemo<SortingState>(() => {
    if (displayOptions.groupBy) {
      return displayOptions.aggOrderBy
        ? [
            {
              id: displayOptions.aggOrderBy,
              desc: displayOptions.aggOrderDir === "desc",
            },
          ]
        : [];
    }
    return displayOptions.orderBy
      ? [
          {
            id: displayOptions.orderBy,
            desc: displayOptions.orderDir === "desc",
          },
        ]
      : [];
  }, [displayOptions]);

  // Parse initial data from view for the form
  const initialData = useMemo(() => {
    if (mode === "update" && view) {
      return {
        title: view.title,
        source: view.source,
        filterString: view.filterString,
        orderBy: view.orderBy,
        orderDir: view.orderDir,
        groupBy: view.groupBy,
        columns: view.columns,
        aggColumns: view.aggColumns,
        aggOrderBy: view.aggOrderBy,
        aggOrderDir: view.aggOrderDir,
      };
    }
    return undefined;
  }, [mode, view]);

  // Fetch preview data
  const { data: tableData, isLoading: isTableLoading } = useExploreInteractions(
    {
      source: formData.source,
      orderBy: displayOptions.orderBy as AllowedInteractionOrderBy,
      orderDir: displayOptions.orderDir,
      filter: filter,
      enabled: !displayOptions.groupBy,
    }
  );

  const { data: aggData, isLoading: isAggLoading } = useExploreAggInteractions({
    source: formData.source,
    groupBy: displayOptions.groupBy,
    orderBy: displayOptions.aggOrderBy as AllowedAggOrderBy,
    orderDir: displayOptions.aggOrderDir ?? "desc",
    filter: filter,
    enabled: !!displayOptions.groupBy,
  });

  const isPreviewLoading = isTableLoading || isAggLoading;

  const customFieldsBySource = useMemo(
    () =>
      formData.source === "conversations"
        ? conversationCustomFieldsForView
        : customFields,
    [formData.source, customFields, conversationCustomFieldsForView]
  );

  // Memoize relevant customFields (only those in displayOptions.columns)
  const relevantCustomFields = useMemo(() => {
    if (!displayOptions.columns) return [];
    return customFieldsBySource.filter((customField) =>
      displayOptions.columns?.includes(customField.id as ExploreColumnType)
    );
  }, [customFieldsBySource, displayOptions.columns]);

  const relevantVisibleColumns = useMemo(() => {
    if (!displayOptions.columns) return undefined;
    const uniqueColumns = Array.from(new Set(displayOptions.columns));
    return uniqueColumns.filter(
      (column) =>
        !customFieldsBySource.some((customField) => customField.id === column)
    );
  }, [customFieldsBySource, displayOptions.columns]);

  // Memoize parameters for useExploreColumns
  const exploreColumnsParams = useMemo(
    () => ({
      source: formData.source as "tickets" | "conversations",
      groupBy: displayOptions.groupBy,
      enrichments: [],
      customFields: relevantCustomFields,
      visibleColumns: relevantVisibleColumns,
      visibleAggColumns: displayOptions.aggColumns,
    }),
    [
      formData.source,
      displayOptions.groupBy,
      relevantCustomFields,
      relevantVisibleColumns,
      displayOptions.aggColumns,
    ]
  );

  // Get columns for the table
  const { columns, aggColumns } = useExploreColumns(exploreColumnsParams);

  if (viewLoading) {
    return (
      <Flex
        width="100%"
        height="100dvh"
        align="center"
        justify="center"
        style={{ backgroundColor: "var(--color-background)" }}
      >
        <Text>Loading view...</Text>
      </Flex>
    );
  }

  if (mode === "update" && !view) {
    return (
      <Flex
        width="100%"
        height="100dvh"
        align="center"
        justify="center"
        style={{ backgroundColor: "var(--color-background)" }}
      >
        <Text size="5" weight="bold">
          View not found
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      width="100%"
      height="100dvh"
      style={{ backgroundColor: "var(--color-background)" }}
    >
      {/* Form Section - Fixed height */}
      <Box flexShrink="0">
        <ExploreNewViewForm
          mode={mode}
          formData={formData}
          displayOptions={displayOptions}
          filters={filters}
          filter={filter}
          errors={errors}
          isLoading={isLoading}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onFieldChange={handleFieldChange}
          onDisplayOptionsChange={handleDisplayOptionsChange}
          onFiltersChange={handleFiltersChange}
          customFields={customFieldsBySource}
        />
      </Box>

      {/* Preview Table Section - Scrollable */}
      <Flex
        direction="column"
        flexGrow="1"
        px="3"
        py="2"
        style={{ overflow: "auto", minHeight: "400px" }}
      >
        {!displayOptions.groupBy && (
          <Table
            data={tableData ?? []}
            columns={columns}
            loading={isPreviewLoading}
            sorting={activeSorting}
            onSortingChange={handleSortingChange}
            hasNextPage={false}
            isFetching={isPreviewLoading}
            isFetchingNextPage={false}
          />
        )}

        {displayOptions.groupBy && (
          <Table
            data={aggData?.results ?? []}
            columns={aggColumns}
            loading={isPreviewLoading}
            sorting={activeSorting}
            onSortingChange={handleSortingChange}
            hasNextPage={false}
            isFetching={isPreviewLoading}
            isFetchingNextPage={false}
          />
        )}
      </Flex>
    </Flex>
  );
}
